import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import {
  EstateInputDataFormatCategory,
  EstateInputDataFormatType,
  EstateInputDataInstance_FieldsFragment,
  UpdateEstateInputDataInstanceInput,
  useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation,
  useEstate_UpdateEstateAndEstateInputDataInstancesMutation,
  useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery,
  useEstateWorkItem_GetEstateWorkItemByIdQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { InvalidConfigurationBanner } from 'pages/template-data-wizard-page/components/invalid-configuration-banner';
import { getEstateUpdateEstateInputData } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateInputDataInstancesForm, {
  EstateInputDataInstanceFormInputValues,
} from './components/estate-input-data-instances-form';
import IncompleteFieldsPromptModal from './components/incomplete-fields-prompt-modal';
import TemplateDataWizardProgressBar from './components/template-data-wizard-progress-bar';
import TemplateDocumentGeneratingScreen from './components/template-document-generating-screen';

const useStyles = makeStyles(() => ({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
    overflow: 'auto',
    height: '100%',
    overflowY: 'auto',
  },
  headerContainer: {
    height: 76,
    padding: '0 80px',
    borderBottom: '1px solid #E0E0E0',
    display: 'flex',
    alignItems: 'center',
    position: 'sticky',
  },
  dataContainer: {
    height: '100%',
    padding: 0,
    margin: 0,
    overflowY: 'auto',
  },
}));

export type GroupedEstateInputDataInstances = Partial<{
  [category in EstateInputDataFormatCategory]: EstateInputDataInstance_FieldsFragment[];
}>;

const TemplateDataWizardPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateWorkItemId } = useParams<Record<string, string>>();
  const [isGeneratingScreenVisible, setIsGeneratingScreenVisible] = useState(false);
  const [isDocumentGenerated, setIsDocumentGenerated] = useState(false);
  const [showIncompleteFieldsPrompt, setShowIncompleteFieldsPrompt] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<EstateInputDataFormatCategory | null>(
    null,
  );

  const { data: estateWorkItemData } = useEstateWorkItem_GetEstateWorkItemByIdQuery({
    variables: { input: { estateWorkItemId, portalId } },
    onError: (e) =>
      showAlert({
        message: `Error getting estate work item by ID: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const { data: estateInputDataInstancesData, refetch: refetchEstateInputDataInstances } =
    useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery({
      skip: !estateWorkItemData?.data?.documentGenerationTemplateId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          estateWorkItemId,
          portalId,
          documentGenerationTemplateId: estateWorkItemData?.data?.documentGenerationTemplateId,
        },
      },
      onError: (e) =>
        showAlert({
          message: `Error getting estate input data instances by estate work item ID: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [updateEstateAndEstateInputDataInstances] =
    useEstate_UpdateEstateAndEstateInputDataInstancesMutation({
      onCompleted: () => refetchEstateInputDataInstances(),
      onError: (e) =>
        showAlert({
          message: `Error updating estate input data instances: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [
    generateDocumentForEstateWorkItem,
    { loading: generateDocumentForEstateWorkItemExecuting },
  ] = useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation({
    onCompleted: () => {
      showToast({
        message: 'Document generated successfully!',
        severity: NotificationSeverity.SUCCESS,
      });
      setIsDocumentGenerated(true);
    },
    onError: (e) => {
      showAlert({
        message: 'Error generating document for estate work item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const checkDocumentGeneration = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (isDocumentGenerated) {
          setIsGeneratingScreenVisible(false);
          history.push(`/home/${portalId}/task/${estateWorkItemId}/template-document`);
        } else {
          checkDocumentGeneration();
        }
      }, 3000);
    };

    if (isGeneratingScreenVisible) {
      checkDocumentGeneration();
    }

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeneratingScreenVisible, isDocumentGenerated]);

  const groupEstateInputDataInstances = useCallback(
    (instances: EstateInputDataInstance_FieldsFragment[]): GroupedEstateInputDataInstances => {
      return instances.reduce((acc, instance) => {
        const category = instance.estateInputDataFormat.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category]!.push(instance);
        return acc;
      }, {} as GroupedEstateInputDataInstances);
    },
    [],
  );

  const groupedInstances = useMemo(() => {
    const filteredInstances = sort(
      estateInputDataInstancesData?.data.filter(
        (instance) => instance.estateInputDataFormat.type !== EstateInputDataFormatType.Document,
      ) || [],
    ).asc((instance) => instance.estateInputDataFormat.name);
    return groupEstateInputDataInstances(filteredInstances);
  }, [estateInputDataInstancesData, groupEstateInputDataInstances]);

  const completedCategories = useMemo(() => {
    return Object.entries(groupedInstances).reduce((acc, [category, instances]) => {
      if (instances.every((instance) => instance.data)) {
        acc.push(category as EstateInputDataFormatCategory);
      }
      return acc;
    }, [] as EstateInputDataFormatCategory[]);
  }, [groupedInstances]);

  useEffect(() => {
    if (!currentCategory) {
      const categories = Object.keys(groupedInstances).sort();
      const nextCategory =
        categories.find(
          (category) => !completedCategories.includes(category as EstateInputDataFormatCategory),
        ) || categories[categories.length - 1];
      setCurrentCategory(nextCategory as EstateInputDataFormatCategory);
    }
  }, [currentCategory, groupedInstances, completedCategories]);

  const currentCategoryInstances = currentCategory ? groupedInstances[currentCategory] : [];
  const categories = Object.keys(groupedInstances).sort() as EstateInputDataFormatCategory[];
  const estateWorkItem = estateWorkItemData?.data;

  useEffect(() => {
    if (estateWorkItem && !estateWorkItem?.documentGenerationTemplateId) {
      showAlert({
        message: 'No document generation template selected for this estate work item',
        severity: NotificationSeverity.ERROR,
      });
      history.replace(`/home/${portalId}/task/${estateWorkItemId}/template-questionnaire`);
    }
  }, [estateWorkItem, history, portalId, estateWorkItemId]);

  const handleSave = async (
    updateEstateInputDataInstanceInputs: EstateInputDataInstanceFormInputValues[],
  ) => {
    const estate = estateWorkItem?.estate;
    if (!estate) {
      return;
    }

    const updateEstateInput = getEstateUpdateEstateInputData(estate);

    return updateEstateAndEstateInputDataInstances({
      variables: {
        input: {
          portalId: portalId,
          updateEstateInput,
          updateEstateInputDataInstancesInput: {
            portalId: portalId,
            updateEstateInputDataInstanceInputs: updateEstateInputDataInstanceInputs.map(
              (value) => ({
                portalId: value.portalId,
                data: value.data,
                id: value.id,
              }),
            ),
          },
        },
      },
    });
  };

  const generateDocument = (keepEmptyPlaceholders: boolean) => {
    if (!estateWorkItem?.documentGenerationTemplateId) {
      setIsGeneratingScreenVisible(true);
      showAlert({
        message: 'No document generation template selected for this estate work item',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    setIsGeneratingScreenVisible(true);
    generateDocumentForEstateWorkItem({
      variables: {
        input: {
          portalId: portalId,
          documentGenerationTemplateId: estateWorkItem.documentGenerationTemplateId,
          estateWorkItemId: estateWorkItemId,
          keepEmptyPlaceholders,
          saveToVault: true,
          updateStatuses: true,
        },
      },
    });
  };

  const handleGenerateClick = async (data: {
    updateEstateInputDataInstanceInputs: UpdateEstateInputDataInstanceInput[];
  }) => {
    setIsGeneratingScreenVisible(true);
    await handleSave(data.updateEstateInputDataInstanceInputs);

    const { data: latestInstanceData } = await refetchEstateInputDataInstances();
    const filteredInstances =
      latestInstanceData?.data.filter(
        (instance) => instance.estateInputDataFormat.type !== EstateInputDataFormatType.Document,
      ) || [];
    const hasEmptyInstances = filteredInstances.some(
      (instance) => !instance.data && !instance.estateInputDataFormat.omitEmptyPlaceholder,
    );
    if (hasEmptyInstances) {
      setIsGeneratingScreenVisible(false);
      setShowIncompleteFieldsPrompt(true);
      return;
    }

    generateDocument(true);
  };

  const handleBackClick = () => {
    if (currentCategory) {
      const currentIndex = categories.indexOf(currentCategory);
      if (currentIndex > 0) {
        setCurrentCategory(categories[currentIndex - 1]);
      }
    }
  };

  const handleSaveAndContinueClick = () => {
    if (currentCategory) {
      const currentIndex = categories.indexOf(currentCategory);
      if (currentIndex < categories.length - 1) {
        setCurrentCategory(categories[currentIndex + 1]);
      }
    }
  };

  const getIsLastCategory = () => {
    if (currentCategory) {
      const currentIndex = categories.indexOf(currentCategory);
      return currentIndex === categories.length - 1;
    }
    return false;
  };

  const isLoading = !estateWorkItemData || !estateInputDataInstancesData;
  const isGeneratingDocument =
    generateDocumentForEstateWorkItemExecuting || isGeneratingScreenVisible;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <HomeLayout>
      <Fade in>
        <div className={classes.container}>
          {!isLoading && !currentCategory && <InvalidConfigurationBanner />}
          <div className={classes.headerContainer}>
            {currentCategory && (
              <TemplateDataWizardProgressBar
                categories={categories}
                currentCategory={currentCategory}
                onChangeCategory={setCurrentCategory}
                completedCategories={completedCategories}
              />
            )}
          </div>
          {isGeneratingDocument && <TemplateDocumentGeneratingScreen />}
          <Box className={classes.dataContainer}>
            {currentCategoryInstances && currentCategory && !isGeneratingDocument && (
              <EstateInputDataInstancesForm
                key={currentCategory}
                estateInputDataInstances={currentCategoryInstances}
                category={currentCategory}
                onSubmit={handleSave}
                portalId={portalId}
                handleBackClick={
                  categories.indexOf(currentCategory) > 0 ? handleBackClick : undefined
                }
                handleSaveAndContinueClick={handleSaveAndContinueClick}
                isLastCategory={getIsLastCategory()}
                onGenerateDocument={handleGenerateClick}
                onExit={() => history.push(`/home/${portalId}/assigned-tasks`)}
              />
            )}
          </Box>
          {showIncompleteFieldsPrompt && (
            <IncompleteFieldsPromptModal
              onClose={() => setShowIncompleteFieldsPrompt(false)}
              onGenerateDocument={generateDocument}
            />
          )}
        </div>
      </Fade>
    </HomeLayout>
  );
};

export default TemplateDataWizardPage;
